<script>
    import { onMount } from "svelte";

    import { fly } from "svelte/transition";

    import {
        SvelteTable,
        PageLoading,
        PageHead,
        SearchInput,
        SearchSelect,
        NotificationModal,
        Button,
        CardLayout,
        ResetFilterLink,
    } from "../../../Components/Common/index.svelte";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getAmountInCurrency,
        getEstimatePaidFor,
        getFormatDate,
        getInsuranceType,
        getNumber,
        getText,
        getTitle,
        getUrlWithoutSearch,
        goToURL,
        isObjectEmpty,
        replaceURL,
        showError,
        showException,
        splitPaymentAmount,
    } from "../../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import { isMobile } from "../../../stores";

    import ReconcileEftCheckbox from "./ReconcileEftCheckbox.svelte";

    export let params = {};
    export let toggleShowEFTsTable = () => {};
    export let selectedReconciliationEftID = Number(0);

    let isLoadingSelectedReconciliationEFT = false,
        isLoadingReceipts = false,
        selectedReconciliationEft = {},
        returnedReceiptsList = [],
        isReconcileModalOpen = false,
        isReconciling = false,
        isReconcileNotificationModalOpen = false;

    let filters = { paidBy: Number(-1), isShowMoreFilters: false };

    let selectedReceiptsIdArr = [],
        selectedReceipts = [],
        checkReceiptString = "";

    let targetAmount = 0,
        sumAmountOfSelectedReceipts = 0;

    let eftHasGroup = false,
        eftHasAccount = false;

    $: displaySumAmount =
        sumAmountOfSelectedReceipts >= 0
            ? getAmountInCurrency(sumAmountOfSelectedReceipts)
            : getAmountInCurrency(0); // avoid floating-point drift

    $: isLoading = isLoadingSelectedReconciliationEFT || isLoadingReceipts;

    $: isTargetAmountMatched = targetAmount == displaySumAmount;

    $: reconcileBtnDisabled = !selectedReceiptsIdArr.length;

    $: columns = [
        {
            key: "id",
            title: "ID",
            headerClass:
                "text-left px-3 w-[5%] cursor-default ml-3 rounded-tl-xl",
            class: "text-left px-3 w-[5%] cursor-default",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "groupName",
            title: "Group",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            isHidden: !eftHasGroup,
            renderValue: (v) => getText(v.groupName),
        },
        {
            key: "accountName",
            title: "Account",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            isHidden: !eftHasAccount,
            renderValue: (v) => getText(v.accountName),
        },
        {
            key: "transactionDate",
            title: "Date Received",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getFormatDate(v.transactionDate),
        },
        {
            key: "amount",
            title: "Amount",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getAmountInCurrency(v.amount),
        },
        {
            key: "transactionId",
            title: "Transaction ID",
            headerClass: "text-left px-3 w-[20%] cursor-default ml-3",
            class: "text-left px-3 w-[20%] cursor-default",
            renderValue: (v) => getText(v.transactionId),
        },
        {
            key: "reconciledDate",
            title: "Reconciled Date",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getFormatDate(v.reconciledDate),
        },
        {
            key: "reconciledBy",
            title: "Reconciled By",
            headerClass: "text-left px-3 w-[30%] cursor-default ml-3",
            class: "text-left px-3 w-[30%] cursor-default",
            renderValue: (v) => _renderUsers(v.userDetail),
        },
        {
            key: "description",
            title: "Description",
            headerClass: "text-left px-3 w-[45%] cursor-default ml-3",
            class: "text-left px-3 w-[45%] cursor-default",
            renderValue: (v) => getText(v.description),
        },
        // {
        //     key: "paymentId",
        //     title: "Payment ID",
        //     headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
        //     class: "text-left px-3 w-[10%] cursor-default",
        //     renderValue: (v) =>
        //         v.paymentIds?.length ? getText(v.paymentIds.join(", ")) : "",
        // },
        {
            key: "receiptId",
            title: "Receipt ID",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) =>
                _renderReceiptIds(v.paymentIds, v.paymentsDetails),
        },
        {
            key: "paymentId",
            title: "Payment ID",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => _renderPaymentIds(v.paymentIds),
        },
        {
            key: "invoiceId",
            title: "Invoice ID",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) =>
                _renderInvoiceIds(v.paymentIds, v.paymentsDetails),
        },
        {
            key: "notes",
            title: "Notes",
            headerClass: "text-left px-3 w-[13%] cursor-default ml-3",
            class: "text-left px-3 w-[13%] cursor-default",
            renderValue: (v) => getText(v.notes),
        },
    ];

    const receiptColumns = [
        {
            key: "checkbox",
            title: "",
            headerClass: "text-left px-3 w-[50px] cursor-default rounded-tl-xl",
            class: (v) =>
                "text-left px-3 w-[50px] cursor-default cursor-default" +
                _getRowBgColor(v.checked),
            renderComponent: {
                component: ReconcileEftCheckbox,
                props: {
                    onToggleSelect: (row) => _updateSelectEftsArr(row),
                },
            },
        },
        {
            key: "receiptId",
            title: "Receipt ID",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[150px]" + _getRowBgColor(v.checked),
            renderValue: (v) => getText(v.id),
        },
        {
            key: "paymentId",
            title: "Payment ID",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[150px]" + _getRowBgColor(v.checked),
            renderValue: (v) => _getPaymentId(v.payments),
        },
        {
            key: "referenceNo",
            title: "Reference No.",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            // renderValue: (v) => getText(v.relNumber),
            renderValue: (v) =>
                _getReferenceNo(v.relId, v.reconciliationDetails),
        },
        {
            key: "receiptType",
            title: "For",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) =>
                _getReceiptType(
                    getEstimatePaidFor(v.relTo),
                    v.relId,
                    v.relNumber,
                ),
        },
        {
            key: "patientEncounter",
            title: "Encounter",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) => _renderPatientEncounter(v.patientEncounterId),
        },
        {
            key: "totalFee",
            title: "Total Fee",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) =>
                _getValueFromReconciliationDetails(
                    v.reconciliationDetails,
                    "totalFee",
                ),
        },
        {
            key: "totalGap",
            title: "Total Gap",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) =>
                _getValueFromReconciliationDetails(
                    v.reconciliationDetails,
                    "totalGap",
                ),
        },
        {
            key: "rebate",
            title: "Rebate",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) =>
                _getValueFromReconciliationDetails(
                    v.reconciliationDetails,
                    "rebate",
                ),
        },
        {
            key: "paymentDate",
            title: "Date Created",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) => getFormatDate(v.paidDate),
        },
        {
            key: "paymentAmount",
            title: "Subtotal Amount",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            value: (v) => _getNetAmount(v.payments),
        },
        {
            key: "processingFee",
            title: "Processing Fee",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 cursor-default w-[200px]" +
                _getRowBgColor(v.checked),
            renderValue: (v) => _getProcessingFee(v.payments),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) => _getGST(v.payments),
        },
        {
            key: "total",
            title: "Total Amount",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) => _getTotal(v.payments),
        },
        {
            key: "insuranceType",
            title: "Insurance Type",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) => _getInsuranceType(v.reconciliationDetails),
        },
        {
            key: "healthFundName",
            title: "Health Fund Name",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) =>
                _getValueFromReconciliationDetails(
                    v.reconciliationDetails,
                    "healthFund",
                ),
        },
        {
            key: "doctor",
            title: "Doctor Name",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) =>
                _getValueFromReconciliationDetails(
                    v.reconciliationDetails,
                    "doctor",
                ),
        },
        {
            key: "doctorProvider",
            title: "Doctor Provider",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) =>
                _getValueFromReconciliationDetails(
                    v.reconciliationDetails,
                    "doctorProvider",
                ),
        },
        {
            key: "patient",
            title: "Patient Name",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) =>
                _getValueFromReconciliationDetails(
                    v.reconciliationDetails,
                    "patient",
                ),
        },
        {
            key: "transactionId",
            title: "Transaction ID",
            headerClass: "text-left px-3 cursor-default",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) => _getTransactionID(v.payments),
        },
        {
            key: "approvalNumber",
            title: "Approval number",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl",
            class: (v) =>
                "text-left px-3 w-[200px]" + _getRowBgColor(v.checked),
            renderValue: (v) => _getApprovalNumber(v.payments),
        },
    ];

    onMount(async () => {
        getUrlWithoutSearch();

        if (selectedReconciliationEftID) {
            if (params.query) updateSearchParamsByQuery(params.query);

            await loadSelectedReconciliationEft(selectedReconciliationEftID);

            await loadReceipts();
        } else toggleShowEFTsTable();
    });

    const loadSelectedReconciliationEft = async (id) => {
        isLoadingSelectedReconciliationEFT = true;

        await apiMethods.getMethod
            .getReconciliationEFTbyId(id)
            .then((res) => {
                _handleReconciliationDetail(res);
            })
            .catch((err) =>
                showException(
                    "Unable to load selected EFT details, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isLoadingSelectedReconciliationEFT = false;
    };

    const loadReceipts = async () => {
        isLoadingReceipts = true;

        let searchParams = {
            "filter[paymentsforreconciliation]": 1,
            ..._getSearchParams(),
        };

        if (selectedReconciliationEft && selectedReconciliationEft.createdDate)
            searchParams["filter[transactionDateFrom]"] =
                selectedReconciliationEft.createdDate?.split("T")[0];

        if (filters.search) searchParams.search = filters.search;

        if (searchParams["filter[eftSearch]"])
            delete searchParams["filter[eftSearch]"];
        if (searchParams.selectedReconciliationEftID)
            delete searchParams.selectedReconciliationEftID;

        if (selectedReceiptsIdArr.length)
            await apiMethods.getMethod
                .getReceipts(0, 999999, {
                    "filter[id]": selectedReceiptsIdArr.join(","),
                })
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to load previously selected receipts, please refresh and try again or contact support...",
                        );
                    else selectedReceipts = [...res.results];
                })
                .catch((err) => showError(err));

        await apiMethods.getMethod
            .getReceipts(0, 999999, searchParams)
            .then((res) => _handleReceipts(res))
            .catch((err) =>
                showException(
                    "Unable to load Receipt details, please refresh the page and try again and contact support if the issue persists.",
                    toggleShowEFTsTable,
                ),
            );

        isLoadingReceipts = false;
    };

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("reconciled="))
                    filters.reconciled = Number(arr[i].split("=")[1]);

                if (arr[i].includes("referenceNo="))
                    filters.referenceNo = arr[i].split("=")[1];

                if (arr[i].includes("relNumber="))
                    filters.relNumber = arr[i].split("=")[1];

                if (arr[i].includes("patientName="))
                    filters.patientName = arr[i].split("=")[1];

                if (arr[i].includes("eftSearchStr="))
                    filters.search = arr[i].split("=")[1];

                if (arr[i].includes("paidBy="))
                    filters.paidBy = Number(arr[i].split("=")[1]);
                else filters.paidBy = Number(-1);

                if (arr[i].includes("amount="))
                    filters.amount = Number(arr[i].split("=")[1]);

                if (arr[i].includes("providerNum="))
                    filters.providerNumber = arr[i].split("=")[1];

                if (arr[i].includes("insuranceFund="))
                    filters.insuranceFund = arr[i].split("=")[1];

                if (arr[i].includes("checkReceipts=")) {
                    checkReceiptString = arr[i].split("=")[1];
                    selectedReceiptsIdArr = checkReceiptString.split(",");
                }

                if (arr[i].includes("checkPrice="))
                    sumAmountOfSelectedReceipts = Number(arr[i].split("=")[1]);

                if (arr[i].includes("totalfeefrom="))
                    filters.totalfeefrom = Number(arr[i].split("=")[1]);

                if (arr[i].includes("totalfeeto="))
                    filters.totalfeeto = Number(arr[i].split("=")[1]);

                if (arr[i].includes("actualgapfeefrom="))
                    filters.actualgapfeefrom = Number(arr[i].split("=")[1]);

                if (arr[i].includes("actualgapfeeto="))
                    filters.actualgapfeeto = Number(arr[i].split("=")[1]);
            }
            updateURL();
        }
    };

    const confirmReconciliation = async () => {
        isReconciling = true;

        await apiMethods.putMethod
            .updateReconciliationEFT({
                id: selectedReconciliationEftID,
                paymentIds: [...selectedReceiptsIdArr],
            })
            .then((res) => {
                if (res.error) showError(res.error);
                else {
                    selectedReceiptsIdArr = [];
                    checkReceiptString = "";
                    toggleShowEFTsTable();
                }
            });

        _closeReconcileModal();

        isReconciling = false;
    };

    const confirmReconcileWithAmountNotMatched = () => {
        _closeReconcileNotificationModal();

        _openReconcileModal();
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "receiptType" && rowData.relId)
            goToURL(`/${rowData.relToName}s/view/${rowData.relId}`);
        else if (key == "doctor" && rowData.doctorId)
            goToURL(`/Account/view/${rowData.doctorId}`);
        else if (key == "patient" && rowData.patientId)
            goToURL(`/Patient/view/${rowData.patientId}`);
        else if (key == "patientEncounter" && rowData.patientEncounterId)
            goToURL(`/PatientEncounter/view/${rowData.patientEncounterId}`);
    };

    const handleSearch = async () => {
        let searchParams = {
            "filter[paymentsforreconciliation]": 1,
            ..._getSearchParams(),
        };

        if (selectedReconciliationEft && selectedReconciliationEft.createdDate)
            searchParams["filter[transactionDateFrom]"] =
                selectedReconciliationEft.createdDate?.split("T")[0];

        if (filters.search) searchParams.search = filters.search;

        if (searchParams["filter[eftSearch]"])
            delete searchParams["filter[eftSearch]"];
        if (searchParams.selectedReconciliationEftID)
            delete searchParams.selectedReconciliationEftID;

        updateURL();

        await apiMethods.getMethod
            .getReceipts(0, 999999, searchParams)
            .then((res) => _handleReceipts(res))
            .catch((err) =>
                showException(
                    "Unable to load Receipt details, please refresh the page and try again and contact support if the issue persists.",
                    toggleShowEFTsTable,
                ),
            );
    };

    const updateURL = () => {
        if (!checkReceiptString)
            replaceURL(`/Reconciliations/tab=EFTs${_getParamsString()}`);
        else
            replaceURL(
                `/Reconciliations/tab=EFTs${_getParamsString()}&checkReceipts=${checkReceiptString}&checkPrice=${getNumber(
                    sumAmountOfSelectedReceipts,
                    2,
                )}`,
            );
    };

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");

    // HELPER FUNCTIONS
    const _handleReconciliationDetail = async (res) => {
        // res = {
        //     id: 1,
        //     source: "string",
        //     createdDate: "2023-08-17T00:30:47.935Z",
        //     updatedDate: "2023-08-17T00:30:47.935Z",
        //     deletedDate: "2023-08-17T00:30:47.935Z",
        //     error: "string",
        //     transactionId: "string",
        //     transactionDate: "2023-08-17T00:30:47.935Z",
        //     description: "string",
        //     amount: 123,
        //     reconciled: false,
        //     paymentId: 1,
        //     eraReconciliationId: 1,
        //     reconciledBy: 1,
        //     reconciledDate: "2023-08-17T00:30:47.935Z",
        //     notes: "string",
        //     paymentIds: [1],
        // };

        if (res.id) {
            selectedReconciliationEft = { ...res };

            await _getUserName();

            targetAmount = getAmountInCurrency(res.amount);

            eftHasAccount = selectedReconciliationEft.accountId > 0;
            eftHasGroup = selectedReconciliationEft.groupId > 0;

            // if (!filters.search && res.description)
            //     filters.search = res.description;
        } else if (res.error) showError(res.error, toggleShowEFTsTable);
        else
            showError(
                "Unable to load selected EFT details, please refresh the page and try again and contact support if the issue persists.",
                toggleShowEFTsTable,
            );
    };

    const _getUserName = async () => {
        if (!selectedReconciliationEft.reconciledBy) return;

        await apiMethods.getMethod
            .getUserById(Math.abs(selectedReconciliationEft.reconciledBy))
            .then((res) => {
                if (res.id)
                    selectedReconciliationEft.userDetail = {
                        name: res.fullName || "",
                        email: res.email || "",
                    };
            });
    };

    const _handleReceipts = async (res) => {
        if (res.results && res.results.length)
            returnedReceiptsList = [...res.results];
        else if (res.error) showError(res.error);
        else returnedReceiptsList = [];

        if (returnedReceiptsList && returnedReceiptsList.length)
            returnedReceiptsList.map((receipt) => (receipt.checked = false));

        if (selectedReceipts.length) {
            if (!returnedReceiptsList.length)
                returnedReceiptsList = [...selectedReceipts];
            else
                selectedReceipts.map((receipt) => {
                    if (
                        !returnedReceiptsList.some(
                            (rReceipt) => rReceipt.id == receipt.id,
                        )
                    )
                        returnedReceiptsList.unshift(receipt);
                });
        }

        if (checkReceiptString) {
            selectedReceiptsIdArr = checkReceiptString
                .split(",")
                .map((receipt) => (receipt = Number(receipt)));

            if (returnedReceiptsList && returnedReceiptsList.length)
                returnedReceiptsList.map((receipt) => {
                    receipt.checked = selectedReceiptsIdArr.some(
                        (receiptId) => receiptId == receipt.id,
                    );
                });
        }
    };

    const _getPaymentId = (payments) => {
        if (!payments || !payments.length) return "-";

        let res = [];

        payments.map((p) => res.push(p.id));

        return res.join(",");
    };

    const _getReferenceNo = (relId, reconciliationDetails = {}) => {
        if (!reconciliationDetails || isObjectEmpty(reconciliationDetails))
            return relId;

        const parsedLastName = reconciliationDetails.patientLastName
            ? reconciliationDetails.patientLastName?.replaceAll(
                  /['",.\-\s]/g,
                  "",
              )
            : reconciliationDetails.patientFirstName?.replaceAll(
                  /['",.\-\s]/g,
                  "",
              );

        return `${parsedLastName}${relId}`;
    };

    const _getReceiptType = (type, relId, relNumber) => {
        return `<div class="cursor-pointer hover:underline">${type} ${relId} (${relNumber})</div>`;
    };

    const _getValueFromReconciliationDetails = (
        reconciliationDetails,
        type = "",
    ) => {
        if (!reconciliationDetails) return "-";

        switch (type) {
            case "totalFee":
                if (!reconciliationDetails.totalFee) return "-";
                else return getAmountInCurrency(reconciliationDetails.totalFee);
            case "totalGap":
                if (!reconciliationDetails.actualGap) return "-";
                else
                    return getAmountInCurrency(reconciliationDetails.actualGap);
            case "rebate":
                if (!reconciliationDetails.rebate) return "-";
                else return getAmountInCurrency(reconciliationDetails.rebate);
            case "healthFund":
                if (!reconciliationDetails.insuranceFundName) return "-";
                else return getText(reconciliationDetails.insuranceFundName);
            case "doctor":
                if (!reconciliationDetails.doctorMiddleName)
                    return `<div class="cursor-pointer hover:underline">${getTitle(
                        reconciliationDetails.doctorTitle,
                    )} ${getText(
                        reconciliationDetails.doctorFirstName,
                    )} ${getText(reconciliationDetails.doctorLastName)}</div>`;
                else
                    return `<div class="cursor-pointer hover:underline">${getTitle(
                        reconciliationDetails.doctorTitle,
                    )} ${getText(
                        reconciliationDetails.doctorFirstName,
                    )} ${getText(
                        reconciliationDetails.doctorMiddleName,
                    )} ${getText(reconciliationDetails.doctorLastName)}</div>`;
            case "doctorProvider":
                if (!reconciliationDetails.doctorProviderNumber) return "-";
                else return getText(reconciliationDetails.doctorProviderNumber);
            case "patient":
                if (!reconciliationDetails.patientMiddleName)
                    return `<div class="cursor-pointer hover:underline">${getTitle(
                        reconciliationDetails.patientTitle,
                    )} ${getText(reconciliationDetails.patientFirstName)} ${
                        reconciliationDetails.patientLastName
                    }</div>`;
                else
                    return `<div class="cursor-pointer hover:underline">${getTitle(
                        reconciliationDetails.patientTitle,
                    )} ${getText(
                        reconciliationDetails.patientFirstName,
                    )} ${getText(reconciliationDetails.patientMiddleName)} ${
                        reconciliationDetails.patientLastName
                    }</div>`;
            default:
                return "";
        }
    };

    const _renderPatientEncounter = (patientEncounterId) => {
        if (!patientEncounterId) return `<div>-</div>`;

        return `<div class="cursor-pointer hover:underline">
                    ${patientEncounterId}
                </div>`;
    };

    const _getNetAmount = (paymentsArr) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(
                `<div>${getAmountInCurrency(
                    splitPaymentAmount(payment).amount,
                )}</div>`,
            );
        });

        return result.join("<br>").toString();
    };

    const _getProcessingFee = (paymentsArr) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(
                `<div>${getAmountInCurrency(
                    splitPaymentAmount(payment).processing,
                )}</div>`,
            );
        });

        return result.join("<br>").toString();
    };

    const _getGST = (paymentsArr) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(
                `<div>${getAmountInCurrency(
                    splitPaymentAmount(payment).gst,
                )}</div>`,
            );
        });

        return result.join("<br>").toString();
    };

    const _getTotal = (paymentsArr) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(
                `<div>${getAmountInCurrency(
                    splitPaymentAmount(payment).total,
                )}</div>`,
            );
        });

        return result.join("<br>").toString();
    };

    const _getInsuranceType = (reconciliationDetails = {}) => {
        if (!reconciliationDetails || isObjectEmpty(reconciliationDetails))
            return "-";

        const { insuranceType } = reconciliationDetails;

        return getInsuranceType(insuranceType);
    };

    const _getTransactionID = (paymentsArr) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(`<div>${getText(payment.transactionId)}</div>`);
        });

        return result.toString().replaceAll(",", "<br>");
    };

    const _getApprovalNumber = (paymentsArr) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(`<div>${getText(payment.approvalNumber)}</div>`);
        });

        return result.toString().replaceAll(",", "<br>");
    };

    const _openReconcileModal = () => (isReconcileModalOpen = true);

    const _closeReconcileModal = () => (isReconcileModalOpen = false);

    const _openReconcileNotificationModal = () =>
        (isReconcileNotificationModalOpen = true);

    const _closeReconcileNotificationModal = () =>
        (isReconcileNotificationModalOpen = false);

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params["filter[eftSearch]"])
                str += `&eftSearchStr=${params["filter[eftSearch]"]}`;

            if (params.selectedReconciliationEftID)
                str += `&selectedReconciliationEftID=${params.selectedReconciliationEftID}`;

            if (Number(params["filter[paidBy]"]) >= 0)
                str += `&paidBy=${params["filter[paidBy]"]}`;

            if (Number(params["filter[amount]"]))
                str += `&amount=${params["filter[amount]"]}`;

            if (params["filter[providerNumber]"])
                str += `&providerNum=${params["filter[providerNumber]"]}`;

            if (params["filter[insuranceFund]"])
                str += `&insuranceFund=${params["filter[insuranceFund]"]}`;

            if (
                params.isShowMoreFilters.toString() == "true" ||
                params.isShowMoreFilters.toString() == "false"
            );
            str += `&toggleShowHideFilters=${params.isShowMoreFilters}`;

            if (params["filter[relNumber]"])
                str += `&relNumber=${params["filter[relNumber]"]}`;

            if (params["filter[patientName]"])
                str += `&patientName=${params["filter[patientName]"]}`;

            if (params["filter[totalfeefrom]"])
                str += `&totalfeefrom=${params["filter[totalfeefrom]"]}`;

            if (params["filter[totalfeeto]"])
                str += `&totalfeeto=${params["filter[totalfeeto]"]}`;

            if (params["filter[actualgapfeefrom]"])
                str += `&actualgapfeefrom=${params["filter[actualgapfeefrom]"]}`;

            if (params["filter[actualgapfeeto]"])
                str += `&actualgapfeeto=${params["filter[actualgapfeeto]"]}`;
        }

        if (filters.referenceNo) str += `&referenceNo=${filters.referenceNo}`;

        return encodeSearchParams(str);
    };

    const _getSearchParams = () => {
        let params = {};

        if (selectedReconciliationEft?.groupId)
            params["filter[groupId]"] = selectedReconciliationEft.groupId;

        if (selectedReconciliationEft?.accountId)
            params["filter[doctorId]"] = selectedReconciliationEft.accountId;

        if (filters.search) params["filter[eftSearch]"] = filters.search;

        if (filters.referenceNo) {
            const numString = filters.referenceNo.match(/\d+/g);

            if (numString.length) params["filter[relId]"] = numString.join("");
        }

        if (filters.relNumber) params["filter[relNumber]"] = filters.relNumber;

        if (filters.patientName)
            params["filter[patientName]"] = filters.patientName;

        if (selectedReconciliationEftID)
            params.selectedReconciliationEftID = selectedReconciliationEftID;

        if (Number(filters.paidBy) >= 0)
            params["filter[paidBy]"] = filters.paidBy;

        if (filters.amount) params["filter[amount]"] = filters.amount;

        if (filters.providerNumber)
            params["filter[providerNumber]"] = filters.providerNumber;

        if (filters.insuranceFund)
            params["filter[insuranceFund]"] = filters.insuranceFund;

        if (filters.totalfeefrom)
            params["filter[totalfeefrom]"] = filters.totalfeefrom;

        if (filters.totalfeeto)
            params["filter[totalfeeto]"] = filters.totalfeeto;

        if (filters.actualgapfeefrom)
            params["filter[actualgapfeefrom]"] = filters.actualgapfeefrom;

        if (filters.actualgapfeeto)
            params["filter[actualgapfeeto]"] = filters.actualgapfeeto;

        if (
            filters.isShowMoreFilters?.toString() == "true" ||
            filters.isShowMoreFilters?.toString() == "false"
        )
            params.isShowMoreFilters = filters.isShowMoreFilters;

        return params;
    };

    const _renderUsers = (userDetail) => {
        if (!userDetail) return "";

        if (!userDetail.name)
            if (!userDetail.email) return "";
            else return getText(userDetail.email);

        if (!userDetail.email) return getText(userDetail.name);

        return `${getText(userDetail.name)} (${getText(userDetail.email)})`;
    };

    const _renderReceiptIds = (idsArr = [], detailsArr = []) => {
        if (!idsArr.length && !detailsArr.length) return "-";

        const res = [];

        idsArr.map((id) => {
            const paymentDetail = detailsArr.find(
                (item) => item.paymentId == id,
            );

            if (paymentDetail) res.push(paymentDetail.receiptNumber ?? "-");
        });

        return res.join("<br>").toString();
    };

    const _renderPaymentIds = (idsArr = []) => {
        if (!idsArr || !idsArr.length) return "-";

        return idsArr.join("<br>").toString();
    };

    const _renderInvoiceIds = (idsArr = [], detailsArr = []) => {
        if (!idsArr.length && !detailsArr.length) return "-";

        const res = [];

        idsArr.map((id) => {
            const paymentDetail = detailsArr.find(
                (item) => item.paymentId == id,
            );

            if (paymentDetail) res.push(paymentDetail.relNumber ?? "-");
        });

        return res.join("<br>").toString();
    };

    const _updateSelectEftsArr = (row) => {
        row.checked = !row.checked;

        const rowAmount = row.payments?.length
            ? row.payments.reduce((sum, { amount }) => (sum += amount), 0)
            : 0;

        if (selectedReceiptsIdArr.includes(row.id)) {
            selectedReceiptsIdArr = selectedReceiptsIdArr.filter(
                (receiptId) => receiptId != row.id,
            );
            selectedReceipts = selectedReceipts.filter(
                (receipt) => receipt.id != row.id,
            );
            sumAmountOfSelectedReceipts -= rowAmount;
            returnedReceiptsList = returnedReceiptsList.map((receipt) => {
                if (receipt.id == row.id) receipt.checked = false;
                return receipt;
            });
        } else {
            selectedReceiptsIdArr = [...selectedReceiptsIdArr, row.id];
            selectedReceipts = [...selectedReceipts, row];
            sumAmountOfSelectedReceipts += rowAmount;
            returnedReceiptsList = returnedReceiptsList.map((receipt) => {
                if (receipt.id == row.id) receipt.checked = true;
                return receipt;
            });
        }

        checkReceiptString = _getCheckboxString(selectedReceiptsIdArr);

        updateURL();
    };

    const _getCheckboxString = (selectedReceiptsIdArr) => {
        if (!selectedReceiptsIdArr.length) return "";

        return selectedReceiptsIdArr.join();
    };

    const _getRowBgColor = (isChecked = false) =>
        isChecked ? " bg-slate-200" : "";
</script>

{#if isLoading}
    <PageLoading />
{:else}
    <div class="flex flex-col flex-1 gap-y-4 relative">
        <PageHead
            title="Reconcile the following EFT"
            backFunc={toggleShowEFTsTable}
        />

        <div class="flex flex-col overflow-y-auto absolute inset-0 gap-4 mt-12">
            <CardLayout title="EFT">
                {#if $isMobile}
                    <div class="h-auto w-minus48">
                        <div class="overflow-x-auto overflow-y-visible">
                            <SvelteTable
                                columns={columns.filter((col) => !col.isHidden)}
                                rows={[{ ...selectedReconciliationEft }]}
                            />
                        </div>
                    </div>
                {:else}
                    <SvelteTable
                        columns={columns.filter((col) => !col.isHidden)}
                        rows={[{ ...selectedReconciliationEft }]}
                    />
                {/if}
            </CardLayout>

            <div class="flex gap-x-4 items-end justify-between">
                <div class="flex gap-x-4 gap-y-2 items-end flex-wrap">
                    <SearchInput
                        bind:value={filters.search}
                        title="Search"
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <SearchInput
                        bind:value={filters.relNumber}
                        title="Quote or Invoice No."
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <SearchInput
                        bind:value={filters.patientName}
                        title="Patient Name"
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <SearchInput
                        bind:value={filters.referenceNo}
                        title="Reference No."
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <SearchSelect
                        bind:value={filters.paidBy}
                        title="Paid By"
                        onChangeFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    >
                        <option value={Number(-1)}>All</option>
                        <option value={Number(0)}>Patient</option>
                        <option value={Number(2)}>Health Fund</option>
                    </SearchSelect>

                    <SearchInput
                        bind:value={filters.amount}
                        type="amount"
                        title="Amount"
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <SearchInput
                        bind:value={filters.totalfeefrom}
                        type="amount"
                        title="Total Fee From"
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <SearchInput
                        bind:value={filters.totalfeeto}
                        type="amount"
                        title="Total Fee to"
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <SearchInput
                        bind:value={filters.actualgapfeefrom}
                        type="amount"
                        title="Gap Fee From"
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <SearchInput
                        bind:value={filters.actualgapfeeto}
                        type="amount"
                        title="Gap Fee to"
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <SearchInput
                        bind:value={filters.providerNumber}
                        title="Provider Number"
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <SearchInput
                        bind:value={filters.insuranceFund}
                        title="Health Fund"
                        inputFunc={handleSearch}
                        inputClass={$isMobile ? "w-64" : "w-36"}
                    />

                    <ResetFilterLink
                        bind:filter={filters}
                        onResetFilter={handleSearch}
                    />

                    {#if !$isMobile}
                        <span class="text-lg">
                            Selected Payment Amount:
                            <em
                                class={sumAmountOfSelectedReceipts > 0
                                    ? targetAmount != displaySumAmount
                                        ? "text-error"
                                        : "text-secondary"
                                    : ""}
                            >
                                {displaySumAmount}
                            </em>
                        </span>
                    {/if}
                </div>

                <Button
                    clickFunc={isTargetAmountMatched
                        ? _openReconcileModal
                        : _openReconcileNotificationModal}
                    isDisabled={reconcileBtnDisabled}
                >
                    Reconcile
                </Button>
            </div>

            {#if $isMobile}
                <span class="text-lg">
                    Selected Payment Amount:
                    <em
                        class={sumAmountOfSelectedReceipts > 0
                            ? targetAmount != displaySumAmount
                                ? "text-error"
                                : "text-secondary"
                            : ""}
                    >
                        {displaySumAmount}
                    </em>
                </span>
            {/if}

            {#if !returnedReceiptsList.length}
                <div>No Available Receipts</div>
            {:else}
                <CardLayout title="Available Receipts">
                    <div
                        class={`w-full overflow-auto relative h-[450px] max-h-[450px] ${
                            $isMobile ? "h-auto w-minus48" : ""
                        }`}
                    >
                        <div class="absolute inset-0 tableContainer">
                            <SvelteTable
                                columns={receiptColumns}
                                rows={returnedReceiptsList}
                                clickCellFunc={({
                                    detail: { event, row, key },
                                }) => {
                                    handleClickCell(event, row, key);
                                }}
                            />
                        </div>
                    </div>
                </CardLayout>
            {/if}
        </div>
    </div>
{/if}

<NotificationModal
    title={"Warning"}
    submitText={"Continue"}
    cancelText={"Cancel"}
    isModalOpen={isReconcileNotificationModalOpen}
    closeModal={_closeReconcileNotificationModal}
    confirmAction={confirmReconcileWithAmountNotMatched}
    closeModalFunction={_closeReconcileNotificationModal}
>
    <p class="leading-6">
        You are attempting to reconcile <em>{displaySumAmount}</em> against an
        EFT transfer of <em>{targetAmount}</em>. Are you sure you want to
        reconcile these amounts? This will amount to an {getNumber(
            Number(
                targetAmount.toString().replaceAll("$", "").replaceAll(",", ""),
            ),
            2,
        ) <
        getNumber(
            Number(
                displaySumAmount
                    .toString()
                    .replaceAll("$", "")
                    .replaceAll(",", ""),
            ),
            2,
        )
            ? "UNDER"
            : "OVER"} payment.
    </p>

    <p class="leading-6">Do you wish to continue?</p>
</NotificationModal>

<NotificationModal
    isModalOpen={isReconcileModalOpen}
    isConfirming={isReconciling}
    closeModal={_closeReconcileModal}
    confirmAction={confirmReconciliation}
    submitText={isReconciling ? "Confirming" : "Reconcile"}
    closeModalFunction={_closeReconcileModal}
>
    <p class="leading-6 text-error">
        Reconcile this EFT with selected Receipt IDs:
    </p>

    <p class="leading-6">{selectedReceiptsIdArr.join()}</p>

    <p class="leading-6 text-error">Selected Payment Amount:</p>

    <p class="leading-6">
        {displaySumAmount}
        {#if !isTargetAmountMatched}
            <span>
                (Selected EFT Amount: {targetAmount})
            </span>
        {/if}
    </p>
</NotificationModal>

<style>
    .tableContainer {
        width: 3000px;
        overflow-x: auto;
    }
</style>
